// Work orders (an auto shop) and repair tickets at vacuum and computer shops: the check-in, the
// parts and labor, the customer's approval of each estimate, the findings, and the printed work
// order. Everything about the vehicle or item is typed in by the counter: Store OS doesn't decode
// VINs or look anything up.
import { Fragment, useEffect, useMemo, useState } from 'react';
import { get, patch, post, del, qs } from '../lib/api';
import { navigate, useDebounced, useLoad } from '../lib/hooks';
import { useSession } from '../lib/session';
import { usePlan } from '../lib/plan';
import { calculateCart, formatCents, formatRatePpm } from '../../shared/money';
import { formatDateTime, formatPhone } from '../../shared/format';
import type { PrecheckValue } from '../../shared/repairs';
import {
  APPROVAL_METHOD_LABELS, COMPUTER_TYPES, INTAKE_ITEMS, INTAKE_VALUE_LABELS, JOB_WORDS, LEFT_WITH_BY_KIND, VACUUM_TYPES, cleanVin, hoursText, laborPrice, vinCheckDigitOk, vinLooksRight,
  type ApprovalMethod, type JobKind, type LineKind,
} from '../../shared/jobs';
import { Badge, Button, Field, Money, MoneyInput, Sheet, Tabs, useAction, useToast } from '../ui/kit';
import { Icon } from '../ui/icons';
import { SignaturePad } from '../ui/signature';
import { CustomerSearch } from './CustomerPicker';
import { ReceiptPolicies, SiteLine, storeLines } from './Receipt';
import { ReceiptMark } from '../ui/legal';
import { Barcode } from '../ui/print';
import type { CartCustomer } from '../lib/cart';

// ---------------------------------------------------------------- shared bits

interface DraftLine { key: string; kind: LineKind; description: string; qty: number; unitCents: number; hours: number; rateCents: number; productId?: string | null; productName?: string | null }

const linePrice = (l: DraftLine) => (l.kind === 'part' ? l.qty * l.unitCents : laborPrice(l.hours, l.rateCents));

function rateFrom(settings: Record<string, string> | undefined): number {
  const v = Number(settings?.labor_rate ?? '');
  return Number.isFinite(v) && v > 0 ? Math.round(v * 100) : 0;
}

/** The shop's service menu ("Name | price" lines), as labor to add in one tap. */
function serviceMenu(settings: Record<string, string> | undefined) {
  return (settings?.service_menu ?? '').split('\n').map((line) => {
    const [name, price] = line.split('|').map((x) => x.trim());
    const cents = Math.round(Number(price) * 100);
    return name && price && Number.isFinite(cents) ? { name, cents } : null;
  }).filter((x): x is { name: string; cents: number } => !!x);
}

/** OK / problem / not checked for each item (the same look as the phone check-in's). */
function IntakeGrid({ kind, value, onChange }: { kind: JobKind; value: Record<string, PrecheckValue>; onChange: (v: Record<string, PrecheckValue>) => void }) {
  return (
    <div className="precheck">
      {INTAKE_ITEMS[kind].map((it) => (
        <div key={it.key} className="pc">
          <span className="small strong">{it.label}</span>
          <span className="opts">
            {(['ok', 'bad', 'na'] as PrecheckValue[]).map((v) => (
              <button key={v} type="button" className={v} aria-pressed={value[it.key] === v} title={INTAKE_VALUE_LABELS[v]} aria-label={`${it.label}: ${INTAKE_VALUE_LABELS[v]}`} onClick={() => onChange({ ...value, [it.key]: v })}>
                {v === 'ok' ? '✓' : v === 'bad' ? '✗' : '?'}
              </button>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
}

/** A part from this store's inventory (it comes out of stock when the ticket is saved). */
function StockSearch({ onPick }: { onPick: (p: { id: string; name: string; price_cents: number }) => void }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const found = useLoad<any[]>(() => (dq.trim().length >= 2 ? get(`/api/products${qs({ q: dq.trim(), limit: 8 })}`) : Promise.resolve([])), [dq]);
  return (
    <div className="stack-sm">
      <div className="search"><Icon.search /><input className="input" placeholder="Search parts in stock (name or SKU)" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search parts in stock" /></div>
      {found.data?.length ? (
        <div className="list">
          {found.data.map((p: any) => (
            <button key={p.id} type="button" className="list-row" onClick={() => { onPick(p); setQ(''); }}>
              <span className="main-col"><span className="t" style={{ display: 'block' }}>{p.name}</span><span className="s" style={{ display: 'block' }}>{[p.sku, p.qty != null ? `${p.qty} here` : null].filter(Boolean).join(' · ')}</span></span>
              <Money cents={p.price_cents} />
            </button>
          ))}
        </div>
      ) : dq.trim().length >= 2 && found.data ? <p className="small muted">Nothing in stock matches. Type the part below instead.</p> : null}
    </div>
  );
}

/** Adding a part or labor to a draft (check-in) or to a saved ticket. */
function LineForm({ initial, rateCents, onSave, saveLabel, busy }: { initial?: Partial<DraftLine>; rateCents: number; onSave: (l: DraftLine) => void; saveLabel: string; busy?: boolean }) {
  const [kind, setKind] = useState<LineKind>(initial?.kind ?? 'labor');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [qty, setQty] = useState(initial?.qty ?? 1);
  const [unit, setUnit] = useState<number | null>(initial?.unitCents ?? null);
  const [hours, setHours] = useState(String(initial?.hours ?? ''));
  const [rate, setRate] = useState<number | null>(initial?.rateCents ?? (rateCents || null));
  const [product, setProduct] = useState<{ id: string; name: string } | null>(initial?.productId ? { id: initial.productId, name: initial.productName ?? '' } : null);
  const h = Number(hours);
  const draft: DraftLine = { key: initial?.key ?? `l${Date.now()}`, kind, description: description.trim(), qty, unitCents: unit ?? 0, hours: Number.isFinite(h) ? h : 0, rateCents: rate ?? 0, productId: product?.id ?? null, productName: product?.name ?? null };
  const price = linePrice(draft);
  const ok = !!draft.description && (kind === 'part' ? qty >= 1 && (unit ?? -1) >= 0 : h > 0 && (rate ?? -1) >= 0);
  return (
    <div className="stack">
      {!initial?.key ? <Tabs label="Part or labor" value={kind} onChange={setKind} tabs={[{ id: 'labor', label: 'Labor' }, { id: 'part', label: 'Part' }]} /> : null}
      {kind === 'part' && !initial?.key ? <StockSearch onPick={(p) => { setProduct(p); setDescription(p.name); setUnit(p.price_cents); }} /> : null}
      {product ? <div className="banner"><Icon.check /><div>From stock: <strong>{product.name}</strong>. It comes out of this store’s stock.</div></div> : null}
      <div className="form-grid">
        <Field label={kind === 'part' ? 'Part' : 'Work done'} className="full">
          <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={120} placeholder={kind === 'part' ? 'e.g. Front brake pads' : 'e.g. Replace front brake pads'} autoFocus />
        </Field>
        {kind === 'part' ? (
          <>
            <Field label="How many"><input className="input" type="number" min={1} max={999} value={qty} onChange={(e) => setQty(Math.max(1, Math.min(999, Math.floor(Number(e.target.value) || 1))))} /></Field>
            <Field label="Price each" hint="taxed"><MoneyInput value={unit} onChange={setUnit} ariaLabel="Price each" /></Field>
          </>
        ) : (
          <>
            <Field label="Hours" hint="like 1.5"><input className="input" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value.replace(/[^\d.]/g, ''))} aria-label="Hours" /></Field>
            <Field label="Rate per hour" hint="labor isn’t taxed"><MoneyInput value={rate} onChange={setRate} ariaLabel="Rate per hour" /></Field>
          </>
        )}
      </div>
      <div className="spread">
        <span className="strong">{formatCents(price)}</span>
        <Button variant="primary" busy={busy} disabled={!ok} onClick={() => onSave(draft)}>{saveLabel}</Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- check-in

export function JobCheckInScreen({ kind, query }: { kind: JobKind; query: URLSearchParams }) {
  const { me, status } = useSession();
  const plan = usePlan();
  const toast = useToast();
  const { run, busy } = useAction();
  const words = JOB_WORDS[kind];
  const [customer, setCustomer] = useState<CartCustomer | null>(null);
  const [year, setYear] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [itemType, setItemType] = useState('');
  const [vin, setVin] = useState('');
  const [plate, setPlate] = useState('');
  const [odometer, setOdometer] = useState('');
  const [serial, setSerial] = useState('');
  const [color, setColor] = useState('');
  const [issue, setIssue] = useState('');
  const [pre, setPre] = useState<Record<string, PrecheckValue>>({});
  const [condition, setCondition] = useState('');
  const [leftWith, setLeftWith] = useState<string[]>([]);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [adding, setAdding] = useState(false);
  const [tech, setTech] = useState('');
  const [promised, setPromised] = useState('');
  const [approval, setApproval] = useState<'now' | 'later' | 'none'>('later');
  const [signer, setSigner] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const rateCents = rateFrom(me?.settings);
  const menu = useMemo(() => serviceMenu(me?.settings), [me?.settings?.service_menu]);
  useEffect(() => {
    const id = query.get('customer');
    if (!id) return;
    get(`/api/customers/${id}`).then((c) => setCustomer({ id: c.id, name: c.name, phone: c.phone, email: c.email, store_credit_cents: c.store_credit_cents })).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (customer && !signer) setSigner(customer.name); }, [customer]);

  const vinClean = cleanVin(vin);
  const vinBad = !!vinClean && !vinLooksRight(vinClean);
  const vinHint = vinClean.length === 17 && vinLooksRight(vinClean) && vinCheckDigitOk(vinClean) === false;
  const rate = me?.location?.tax_rate_ppm ?? 0;
  const estimate = useMemo(() => calculateCart(lines.map((l) => ({ qty: 1, unitPriceCents: linePrice(l), taxable: l.kind === 'part' })), 0, rate), [lines, rate]);
  const partsTotal = lines.filter((l) => l.kind === 'part').reduce((s, l) => s + linePrice(l), 0);
  const yearN = Number(year);
  const missing = !customer ? 'the customer' : !brand.trim() ? (kind === 'vehicle' ? 'the make' : 'the brand') : !model.trim() ? 'the model' : !issue.trim() ? 'the problem' : vinBad ? 'a VIN that looks right' : approval === 'now' && !lines.length ? 'the parts and labor to approve' : approval === 'now' && (!signer.trim() || !signature) ? 'the customer’s signature' : null;
  const techs = status?.device?.employees ?? [];

  const save = async () => {
    if (missing || !customer) return toast(`Add ${missing ?? 'the customer'} first.`, 'error');
    const body = {
      customerId: customer.id, jobKind: kind, brand: brand.trim(), model: kind !== 'vehicle' && itemType ? `${model.trim()} (${itemType})` : model.trim(),
      color: color.trim() || null, serial: kind !== 'vehicle' ? serial.trim() || null : null,
      vehicleYear: kind === 'vehicle' && year ? yearN : null, vin: kind === 'vehicle' ? vinClean || null : null, plate: kind === 'vehicle' ? plate.trim() || null : null,
      odometerIn: kind === 'vehicle' && odometer ? Math.floor(Number(odometer)) : null,
      issue: issue.trim(), conditionNotes: condition.trim() || null, precheck: pre, leftWith,
      technicianId: plan.has('tech_assignment') ? tech || null : null,
      promisedAt: plan.has('tech_assignment') && promised ? new Date(promised).getTime() : null,
      // Texting the customer is West Coast Wireless's own (the counter's texting service).
      notifySms: plan.home,
      lines: lines.map((l) => ({
        kind: l.kind, description: l.description, priceCents: linePrice(l), qty: l.kind === 'part' ? l.qty : null, hours: l.kind === 'labor' ? l.hours : null,
        partsCents: l.kind === 'part' ? linePrice(l) : 0, productId: l.productId ?? null,
      })),
    };
    const r = await run(() => post('/api/repairs', body));
    if (!r) return;
    if (approval === 'now' && lines.length) {
      await run(() => post(`/api/repairs/${r.id}/approvals`, { decision: 'approved', method: 'in_person', name: signer.trim(), signature }));
    } else if (approval === 'later' && lines.length) {
      await run(() => post(`/api/repairs/${r.id}/status`, { status: 'waiting_customer', note: 'Estimate given: waiting for the customer’s OK' }));
    }
    toast(`${r.ticket} saved.`, 'good');
    navigate(`/repairs/${r.id}?created=1`);
  };

  return (
    <div className="page job-checkin">
      <div className="page-head"><h1>{words.checkIn}</h1></div>
      <section className="card stack">
        <h2>1. Customer</h2>
        <CustomerSearch value={customer} onChange={setCustomer} />
      </section>

      <section className="card stack">
        <h2>2. {words.Item}</h2>
        {kind === 'vehicle' ? (
          <div className="form-grid">
            <Field label="Year"><input className="input" inputMode="numeric" value={year} onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="e.g. 2018" aria-label="Year" /></Field>
            <Field label="Make"><input className="input" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. Toyota" aria-label="Make" /></Field>
            <Field label="Model"><input className="input" value={model} onChange={(e) => setModel(e.target.value)} placeholder="e.g. Camry SE" aria-label="Model" /></Field>
            <Field label="Color" hint="optional"><input className="input" value={color} onChange={(e) => setColor(e.target.value)} aria-label="Color" /></Field>
            <Field label="VIN" hint={vinBad ? '17 letters and numbers, no I, O or Q' : vinHint ? 'Check it: the check digit doesn’t match (fine for vehicles made outside North America)' : 'typed in; optional'} className="full">
              <input className="input num" value={vin} onChange={(e) => setVin(e.target.value.toUpperCase())} maxLength={20} aria-invalid={vinBad} aria-label="VIN" />
            </Field>
            <Field label="Plate" hint="optional"><input className="input" value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} maxLength={12} aria-label="Plate" /></Field>
            <Field label="Odometer in" hint="miles"><input className="input" inputMode="numeric" value={odometer} onChange={(e) => setOdometer(e.target.value.replace(/\D/g, '').slice(0, 7))} aria-label="Odometer in" /></Field>
          </div>
        ) : (
          <div className="form-grid">
            <Field label="Kind">
              <select className="select" value={itemType} onChange={(e) => setItemType(e.target.value)} aria-label="Kind">
                <option value="">Pick one</option>
                {(kind === 'vacuum' ? VACUUM_TYPES : COMPUTER_TYPES).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Brand"><input className="input" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder={kind === 'vacuum' ? 'e.g. Dyson' : 'e.g. Dell'} aria-label="Brand" /></Field>
            <Field label="Model"><input className="input" value={model} onChange={(e) => setModel(e.target.value)} placeholder={kind === 'vacuum' ? 'e.g. V11' : 'e.g. XPS 13'} aria-label="Model" /></Field>
            <Field label="Serial number" hint="optional"><input className="input" value={serial} onChange={(e) => setSerial(e.target.value.toUpperCase())} aria-label="Serial number" /></Field>
            <Field label="Color" hint="optional"><input className="input" value={color} onChange={(e) => setColor(e.target.value)} aria-label="Color" /></Field>
          </div>
        )}
      </section>

      <section className="card stack">
        <h2>3. What’s wrong</h2>
        <Field label={kind === 'vehicle' ? 'What the customer says' : 'The problem'}><textarea className="input" rows={3} value={issue} onChange={(e) => setIssue(e.target.value)} aria-label="The problem" /></Field>
        <div className="small strong muted">Drop-off check</div>
        <IntakeGrid kind={kind} value={pre} onChange={setPre} />
        <Field label="Condition notes" hint="scratches, dents, missing pieces"><input className="input" value={condition} onChange={(e) => setCondition(e.target.value)} aria-label="Condition notes" /></Field>
        <div className="small strong muted">Left with it</div>
        <div className="chips" aria-label="Left with it">
          {LEFT_WITH_BY_KIND[kind].map((x) => (
            <button key={x} type="button" className="chip" aria-pressed={leftWith.includes(x)} onClick={() => setLeftWith(leftWith.includes(x) ? leftWith.filter((y) => y !== x) : [...leftWith, x])}>{x}</button>
          ))}
        </div>
      </section>

      <section className="card stack">
        <div className="spread"><h2>4. Estimate: parts and labor</h2><Button icon="plus" onClick={() => setAdding(true)}>Add part or labor</Button></div>
        {menu.length ? (
          <div className="chips" aria-label="Service menu">
            {menu.map((m) => (
              <button key={m.name} type="button" className="chip" onClick={() => setLines([...lines, { key: `m${Date.now()}${m.name}`, kind: 'labor', description: m.name, qty: 1, unitCents: 0, hours: 1, rateCents: m.cents }])}>{m.name} · {formatCents(m.cents)}</button>
            ))}
          </div>
        ) : null}
        {lines.length ? (
          <div className="list">
            {lines.map((l) => (
              <div key={l.key} className="list-row" style={{ cursor: 'default' }}>
                <Badge tone={l.kind === 'part' ? 'blue' : 'plain'}>{l.kind === 'part' ? `Part × ${l.qty}` : `Labor ${hoursText(Math.round(l.hours * 100))}`}</Badge>
                <span className="main-col"><span className="t" style={{ display: 'block' }}>{l.description}</span><span className="s" style={{ display: 'block' }}>{l.kind === 'part' ? `${formatCents(l.unitCents)} each${l.productName ? ' · from stock' : ''}` : `${formatCents(l.rateCents)} an hour`}</span></span>
                <Money cents={linePrice(l)} className="strong" />
                <button type="button" className="icon-btn" aria-label={`Remove ${l.description}`} onClick={() => setLines(lines.filter((x) => x.key !== l.key))}><Icon.close /></button>
              </div>
            ))}
            <div className="repair-totals" aria-label="Estimate">
              <div className="rt-row"><span>Parts</span><span className="num">{formatCents(partsTotal)}</span></div>
              <div className="rt-row"><span>Labor</span><span className="num">{formatCents(estimate.subtotal - partsTotal)}</span></div>
              <div className="rt-row"><span>Sales tax {formatRatePpm(rate)} on parts</span><span className="num">{formatCents(estimate.tax)}</span></div>
              <div className="rt-row rt-total"><span>Estimate</span><span className="num">{formatCents(estimate.total)}</span></div>
            </div>
          </div>
        ) : <p className="small muted">No parts or labor yet. You can also add them after the {words.item} is looked at.</p>}
      </section>

      {plan.has('tech_assignment') ? (
        <section className="card stack">
          <h2>5. Who and when</h2>
          <div className="form-grid">
            <Field label="Technician" hint="optional">
              <select className="select" value={tech} onChange={(e) => setTech(e.target.value)} aria-label="Technician">
                <option value="">Anyone</option>
                {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>
            <Field label="Ready by" hint="optional"><input className="input" type="datetime-local" value={promised} onChange={(e) => setPromised(e.target.value)} aria-label="Ready by" /></Field>
          </div>
        </section>
      ) : null}

      <section className="card stack">
        <h2>{plan.has('tech_assignment') ? '6' : '5'}. The customer’s OK</h2>
        <Tabs label="The customer’s OK" value={approval} onChange={(v) => setApproval(v === 'now' && !lines.length ? 'later' : v)} tabs={[{ id: 'now', label: 'Approve now (sign)' }, { id: 'later', label: 'Get it later' }, { id: 'none', label: 'No estimate yet' }]} />
        {approval === 'now' && !lines.length ? <p className="small muted">Add the parts and labor first.</p> : null}
        {approval === 'now' && lines.length ? (
          <div className="stack-sm">
            <p className="small">I approve this estimate of <strong>{formatCents(estimate.total)}</strong>. No other work or charges without my OK.</p>
            <Field label="Name"><input className="input" value={signer} onChange={(e) => setSigner(e.target.value)} aria-label="Name of the person approving" /></Field>
            <SignaturePad onChange={setSignature} label="Customer signs here" />
          </div>
        ) : approval === 'later' ? (
          <p className="small muted">{lines.length ? 'It waits as “Needs customer OK” until you record their answer (by phone, text, email or in person).' : `Record their OK on the ${words.job === 'work order' ? 'work order' : 'ticket'} once you’ve priced the work.`}</p>
        ) : null}
      </section>

      <div className="sticky-actions">
        {missing ? <span className="small muted">Add {missing}.</span> : <span className="small strong">{lines.length ? `Estimate ${formatCents(estimate.total)}` : 'Ready to save'}</span>}
        <Button variant="primary" size="lg" busy={busy} disabled={!!missing} onClick={save}>{kind === 'vehicle' ? 'Save work order' : 'Check in'}</Button>
      </div>

      {adding ? (
        <Sheet title="Add part or labor" onClose={() => setAdding(false)}>
          <LineForm rateCents={rateCents} saveLabel="Add" onSave={(l) => { setLines([...lines, l]); setAdding(false); }} />
        </Sheet>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- on the ticket

/** The vehicle (or vacuum, or computer): what was written down, and changing it. */
export function JobItemCard({ r, work, onChanged }: { r: any; work: boolean; onChanged: (res: any) => void }) {
  const [editing, setEditing] = useState(false);
  const v = r.job.vehicle;
  const vinWarn = v?.vin && v.vin.length === 17 && vinCheckDigitOk(v.vin) === false;
  return (
    <div className="card stack-sm">
      <div className="spread"><h3>{JOB_WORDS[r.job.kind as JobKind].Item}</h3>{work ? <Button size="sm" variant="ghost" icon="edit" onClick={() => setEditing(true)}>Edit</Button> : null}</div>
      <dl className="kv">
        <dt>{r.job.kind === 'vehicle' ? 'Vehicle' : 'Item'}</dt><dd className="strong">{r.device_name}</dd>
        {r.color ? <><dt>Color</dt><dd>{r.color}</dd></> : null}
        {v ? (
          <>
            <dt>VIN</dt><dd className="num">{v.vin ?? '—'}{vinWarn ? <span className="small" style={{ color: 'var(--amber)', display: 'block' }}>The check digit doesn’t match: check it against the vehicle.</span> : null}</dd>
            <dt>Plate</dt><dd>{v.plate ?? '—'}</dd>
            <dt>Odometer in</dt><dd>{v.odometer_in != null ? `${v.odometer_in.toLocaleString('en-US')} mi` : '—'}</dd>
            <dt>Odometer out</dt><dd>{v.odometer_out != null ? `${v.odometer_out.toLocaleString('en-US')} mi` : '—'}</dd>
          </>
        ) : r.serial ? <><dt>Serial</dt><dd className="num">{r.serial}</dd></> : null}
        <dt>Problem</dt><dd>{r.issue}</dd>
        {r.condition_notes ? <><dt>Condition</dt><dd>{r.condition_notes}</dd></> : null}
        {r.left_with.length ? <><dt>Left with it</dt><dd>{r.left_with.join(', ')}</dd></> : null}
        <dt>Checked in</dt><dd>{formatDateTime(r.created_at)}{r.created_by_name ? ` by ${r.created_by_name}` : ''}</dd>
        {r.promised_at ? <><dt>Ready by</dt><dd>{formatDateTime(r.promised_at)}</dd></> : null}
        <dt>Technician</dt><dd>{r.technician?.name ?? 'Anyone'}</dd>
        <dt>Store</dt><dd>{r.location_name}</dd>
      </dl>
      {editing ? <JobDetailsSheet r={r} onClose={() => setEditing(false)} onDone={(res) => { onChanged(res); setEditing(false); }} /> : null}
    </div>
  );
}

function JobDetailsSheet({ r, onClose, onDone }: { r: any; onClose: () => void; onDone: (res: any) => void }) {
  const { run, busy } = useAction();
  const v = r.job.vehicle;
  const [f, setF] = useState({
    year: v?.year ? String(v.year) : '', brand: r.brand, model: r.model, color: r.color ?? '', vin: v?.vin ?? '', plate: v?.plate ?? '',
    odoIn: v?.odometer_in != null ? String(v.odometer_in) : '', odoOut: v?.odometer_out != null ? String(v.odometer_out) : '', serial: r.serial ?? '', issue: r.issue, condition: r.condition_notes ?? '',
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const vehicle = r.job.kind === 'vehicle';
  const save = async () => {
    const body: any = { brand: f.brand.trim(), model: f.model.trim(), color: f.color.trim() || null, issue: f.issue.trim(), conditionNotes: f.condition.trim() || null };
    if (vehicle) Object.assign(body, { vehicleYear: f.year ? Number(f.year) : null, vin: cleanVin(f.vin) || null, plate: f.plate.trim() || null, odometerIn: f.odoIn ? Number(f.odoIn) : null, odometerOut: f.odoOut ? Number(f.odoOut) : null });
    else body.serial = f.serial.trim() || null;
    const res = await run(() => patch(`/api/repairs/${r.id}`, body), { success: 'Saved.' });
    if (res) onDone(res);
  };
  return (
    <Sheet title={`Edit ${r.ticket}`} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} onClick={save}>Save</Button></>}>
      <div className="form-grid">
        {vehicle ? <Field label="Year"><input className="input" inputMode="numeric" value={f.year} onChange={set('year')} /></Field> : null}
        <Field label={vehicle ? 'Make' : 'Brand'}><input className="input" value={f.brand} onChange={set('brand')} /></Field>
        <Field label="Model"><input className="input" value={f.model} onChange={set('model')} /></Field>
        <Field label="Color"><input className="input" value={f.color} onChange={set('color')} /></Field>
        {vehicle ? (
          <>
            <Field label="VIN" className="full"><input className="input num" value={f.vin} onChange={(e) => setF({ ...f, vin: e.target.value.toUpperCase() })} /></Field>
            <Field label="Plate"><input className="input" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} /></Field>
            <Field label="Odometer in"><input className="input" inputMode="numeric" value={f.odoIn} onChange={set('odoIn')} /></Field>
            <Field label="Odometer out" hint="at pickup"><input className="input" inputMode="numeric" value={f.odoOut} onChange={set('odoOut')} /></Field>
          </>
        ) : <Field label="Serial number"><input className="input" value={f.serial} onChange={set('serial')} /></Field>}
        <Field label="Problem" className="full"><textarea className="input" rows={3} value={f.issue} onChange={set('issue')} /></Field>
        <Field label="Condition notes" className="full"><input className="input" value={f.condition} onChange={set('condition')} /></Field>
      </div>
    </Sheet>
  );
}

/** What the technician found (inspection findings at an auto shop). */
export function DiagnosisCard({ r, work, onChanged }: { r: any; work: boolean; onChanged: (res: any) => void }) {
  const { run, busy } = useAction();
  const [text, setText] = useState<string | null>(null);
  const title = r.job.kind === 'vehicle' ? 'Inspection findings' : 'Diagnosis';
  const save = async () => {
    const res = await run(() => patch(`/api/repairs/${r.id}`, { diagnosis: (text ?? '').trim() || null }), { success: 'Saved.' });
    if (res) { onChanged(res); setText(null); }
  };
  return (
    <div className="card stack-sm">
      <div className="spread"><h3>{title}</h3>{work && text === null ? <Button size="sm" variant="ghost" icon="edit" onClick={() => setText(r.job.diagnosis ?? '')}>{r.job.diagnosis ? 'Edit' : 'Add'}</Button> : null}</div>
      {text !== null ? (
        <>
          <textarea className="input" rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={r.job.kind === 'vehicle' ? 'Codes read, what was measured, what’s worn or broken' : 'What was found, and what it needs'} aria-label={title} autoFocus />
          <div className="row" style={{ gap: 8 }}><Button variant="primary" busy={busy} onClick={save}>Save</Button><Button variant="ghost" onClick={() => setText(null)}>Cancel</Button></div>
        </>
      ) : r.job.diagnosis ? <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{r.job.diagnosis}</p> : <p className="small muted" style={{ margin: 0 }}>Nothing written yet.</p>}
    </div>
  );
}

/** Each estimate the customer answered: approved or declined, how, by whom, the total and their signature. */
export function ApprovalsCard({ r, work, onChanged }: { r: any; work: boolean; onChanged: (res: any) => void }) {
  const [recording, setRecording] = useState(false);
  const j = r.job;
  return (
    <div className="card stack-sm">
      <div className="spread"><h3>Customer approval</h3>{work && r.lines.length ? <Button size="sm" icon="check" onClick={() => setRecording(true)}>Record their answer</Button> : null}</div>
      {r.lines.length && j.needs_approval ? (
        <div className="banner warn" role="status"><Icon.info /><div>{j.approved_total_cents != null ? `The estimate is now ${formatCents(r.totals.total_cents)}; ${formatCents(j.approved_total_cents)} was approved. Get their OK for the rest before doing it.` : `Not approved yet: ${formatCents(r.totals.total_cents)}. Get their OK before starting the work.`}</div></div>
      ) : r.lines.length ? <div className="banner good" role="status"><Icon.check /><div>Approved: {formatCents(j.approved_total_cents)}.</div></div> : <p className="small muted" style={{ margin: 0 }}>Add the parts and labor, then record the customer’s answer.</p>}
      {j.approvals.length ? (
        <div className="list">
          {j.approvals.map((a: any) => (
            <div key={a.id} className="list-row" style={{ cursor: 'default', alignItems: 'flex-start' }}>
              <Badge tone={a.decision === 'approved' ? 'green' : 'red'}>{a.decision === 'approved' ? 'Approved' : 'Declined'}</Badge>
              <span className="main-col">
                <span className="t" style={{ display: 'block' }}>{formatCents(a.total_cents)} · {a.method_label} · {a.customer_name}</span>
                <span className="s" style={{ display: 'block' }}>{formatDateTime(a.created_at)}{a.recorded_by ? ` · recorded by ${a.recorded_by}` : ''}{a.note ? ` · ${a.note}` : ''}</span>
              </span>
              {a.signature_url ? <img className="sig-thumb" src={a.signature_url} alt={`${a.customer_name}’s signature`} /> : null}
            </div>
          ))}
        </div>
      ) : null}
      {recording ? <ApprovalSheet r={r} onClose={() => setRecording(false)} onDone={(res) => { onChanged(res); setRecording(false); }} /> : null}
    </div>
  );
}

export function ApprovalSheet({ r, onClose, onDone }: { r: any; onClose: () => void; onDone: (res: any) => void }) {
  const { run, busy } = useAction();
  const [decision, setDecision] = useState<'approved' | 'declined'>('approved');
  const [method, setMethod] = useState<ApprovalMethod>('phone');
  const [name, setName] = useState<string>(r.customer.name);
  const [note, setNote] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const needSig = method === 'in_person' && decision === 'approved';
  const save = async () => {
    const res = await run(() => post(`/api/repairs/${r.id}/approvals`, { decision, method, name: name.trim(), note: note.trim() || null, signature: needSig ? signature : null }), { success: decision === 'approved' ? 'Approval saved.' : 'Saved: declined.' });
    if (res) onDone(res);
  };
  return (
    <Sheet title="The customer’s answer" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} disabled={!name.trim() || (needSig && !signature)} onClick={save}>Save</Button></>}>
      <div className="stack">
        <p style={{ margin: 0 }}>Estimate: <strong>{formatCents(r.totals.total_cents)}</strong> ({r.lines.length} line{r.lines.length === 1 ? '' : 's'}).</p>
        <Tabs label="Answer" value={decision} onChange={setDecision} tabs={[{ id: 'approved', label: 'Approved' }, { id: 'declined', label: 'Declined' }]} />
        <Field label="How">
          <select className="select" value={method} onChange={(e) => setMethod(e.target.value as ApprovalMethod)} aria-label="How they answered">
            {(Object.keys(APPROVAL_METHOD_LABELS) as ApprovalMethod[]).map((m) => <option key={m} value={m}>{APPROVAL_METHOD_LABELS[m]}</option>)}
          </select>
        </Field>
        <Field label="Who answered"><input className="input" value={name} onChange={(e) => setName(e.target.value)} aria-label="Who answered" /></Field>
        <Field label="Note" hint="optional"><input className="input" value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note" /></Field>
        {needSig ? <><p className="small" style={{ margin: 0 }}>I approve this estimate of {formatCents(r.totals.total_cents)}. No other work or charges without my OK.</p><SignaturePad onChange={setSignature} label="Customer signs here" /></> : null}
      </div>
    </Sheet>
  );
}

/** Parts and labor on a saved ticket: add, change (how many, hours, price) or remove. */
export function JobLinesCard({ r, work, onChanged, children }: { r: any; work: boolean; onChanged: (res: any) => void; children?: React.ReactNode }) {
  const { me } = useSession();
  const { run, busy } = useAction();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const rateCents = rateFrom(me?.settings);
  const editable = work && !r.paid;
  const add = async (l: DraftLine) => {
    const res = await run(() => post(`/api/repairs/${r.id}/lines`, { kind: l.kind, description: l.description, priceCents: linePrice(l), qty: l.kind === 'part' ? l.qty : null, hours: l.kind === 'labor' ? l.hours : null, partsCents: l.kind === 'part' ? linePrice(l) : 0, productId: l.productId ?? null }), { success: 'Added.' });
    if (res) { onChanged(res); setAdding(false); }
  };
  const change = async (line: any, l: DraftLine) => {
    const res = await run(() => patch(`/api/repairs/${r.id}/lines/${line.id}`, { description: l.description, priceCents: linePrice(l), qty: line.kind === 'part' ? l.qty : undefined, hours: line.kind === 'labor' ? l.hours : undefined, partsCents: line.kind === 'part' ? linePrice(l) : line.kind === 'labor' ? 0 : undefined }), { success: 'Saved.' });
    if (res) { onChanged(res); setEditing(null); }
  };
  const remove = async (line: any) => {
    const res = await run(() => del(`/api/repairs/${r.id}/lines/${line.id}`), { success: 'Removed.' });
    if (res) { onChanged(res); setEditing(null); }
  };
  const parts = r.lines.filter((l: any) => l.kind === 'part' || (!l.kind && l.parts_cents));
  const labor = r.lines.filter((l: any) => !parts.includes(l));
  const group = (title: string, list: any[]) => list.length ? (
    <>
      <div className="small strong muted" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</div>
      <div className="list">
        {list.map((l: any) => (
          <button key={l.id} type="button" className="list-row" onClick={() => (editable ? setEditing(l) : undefined)} style={{ cursor: editable ? 'pointer' : 'default' }}>
            <span className="main-col">
              <span className="t" style={{ display: 'block' }}>{l.description}</span>
              <span className="s" style={{ display: 'block' }}>
                {l.kind === 'part' ? `${l.qty ?? 1} × ${formatCents(Math.round(l.price_cents / (l.qty || 1)))}${l.product ? ' · from stock' : ''}` : l.kind === 'labor' ? `${hoursText(Math.round((l.hours ?? 0) * 100))} · ${l.hours ? `${formatCents(Math.round(l.price_cents / l.hours))} an hour` : ''}` : l.parts_cents ? `Taxed parts ${formatCents(l.parts_cents)}` : 'Labor (no tax)'}
              </span>
            </span>
            <span className="row" style={{ gap: 6 }}><Money cents={l.price_cents} className="strong" />{editable ? <span className="line-edit" aria-hidden="true"><Icon.edit /></span> : null}</span>
          </button>
        ))}
      </div>
    </>
  ) : null;
  return (
    <div className="card stack-sm">
      <div className="spread"><h3>Parts and labor</h3>{editable ? <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>Add part or labor</Button> : null}</div>
      {r.paid ? <p className="small muted">Paid, so the prices are locked. To change them, refund receipt #{r.paid.receipt_no} first.</p> : null}
      {r.lines.length ? <>{group('Parts', parts)}{group('Labor', labor)}</> : <p className="muted">Nothing priced yet.{editable ? ' Tap “Add part or labor”.' : ''}</p>}
      <dl className="kv">
        <dt>Parts</dt><dd><Money cents={r.totals.parts_cents} /></dd>
        <dt>Labor</dt><dd><Money cents={r.totals.labor_cents} /></dd>
        <dt>{r.totals.tax_removed ? 'Tax removed at checkout' : <>Sales tax {formatRatePpm(r.totals.tax_rate_ppm)} on parts</>}</dt><dd><Money cents={r.totals.tax_cents} /></dd>
        <dt className="strong">{r.paid ? 'Total' : 'Estimate'}</dt><dd className="strong"><Money cents={r.totals.total_cents} /></dd>
        {children}
      </dl>
      {adding ? <Sheet title="Add part or labor" onClose={() => setAdding(false)}><LineForm rateCents={rateCents} saveLabel="Add" busy={busy} onSave={add} /></Sheet> : null}
      {editing ? (
        <Sheet title={editing.description} onClose={() => setEditing(null)} footer={<><Button variant="danger" busy={busy} onClick={() => remove(editing)}>Remove</Button><span className="grow" /><Button onClick={() => setEditing(null)}>Cancel</Button></>}>
          <LineForm
            busy={busy}
            rateCents={rateCents}
            saveLabel="Save"
            initial={{
              key: editing.id, kind: editing.kind === 'part' ? 'part' : 'labor', description: editing.description, qty: editing.qty ?? 1,
              unitCents: editing.kind === 'part' ? Math.round(editing.price_cents / (editing.qty || 1)) : 0, hours: editing.hours ?? 1,
              rateCents: editing.kind === 'labor' && editing.hours ? Math.round(editing.price_cents / editing.hours) : editing.price_cents,
              productId: editing.product?.id ?? null, productName: editing.product?.name ?? null,
            }}
            onSave={(l) => change(editing, l)}
          />
        </Sheet>
      ) : null}
    </div>
  );
}

/** The drop-off check on a saved ticket, with this kind of job's items. */
export function JobIntakeCard({ r }: { r: any }) {
  const items = INTAKE_ITEMS[r.job.kind as JobKind].filter((it) => r.precheck[it.key]);
  if (!items.length) return null;
  return (
    <div className="card stack-sm">
      <h3>Drop-off check</h3>
      <dl className="kv">
        {items.map((it) => <Fragment key={it.key}><dt>{it.label}</dt><dd className={r.precheck[it.key] === 'bad' ? 'overdue' : ''}>{INTAKE_VALUE_LABELS[r.precheck[it.key] as PrecheckValue]}</dd></Fragment>)}
      </dl>
    </div>
  );
}

// ---------------------------------------------------------------- the printed work order

/** The full-page work order (or repair ticket): the vehicle or item, findings, parts and labor, approvals, and where to sign. */
export function WorkOrderLetter({ p }: { p: any }) {
  const j = p.job;
  const kind = j.kind as JobKind;
  const v = j.vehicle;
  const parts = (p.line_details as any[]).filter((l) => l.kind === 'part');
  const labor = (p.line_details as any[]).filter((l) => l.kind !== 'part');
  // Every answer the customer gave, oldest first (the first OK, then any more work they approved or declined).
  const answers = [...(j.approvals as any[])].reverse();
  const checks = INTAKE_ITEMS[kind].filter((it) => p.precheck?.[it.key]).map((it) => `${it.label}: ${INTAKE_VALUE_LABELS[p.precheck[it.key] as PrecheckValue]}`);
  const paid = !!p.paid && !p.paid.refunded;
  return (
    <div className="invoice work-order">
      <div className="ih">
        <div>
          {p.logo_url ? <img src={p.logo_url} alt={p.business_name} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} /> : null}
          <div className="biz">{p.business_name}</div>
          <div>{p.store.name}</div>
          {storeLines(p.store).map((l: string) => <div key={l}>{l}</div>)}
          <div>{[p.store.phone ? formatPhone(p.store.phone) : null, p.website].filter(Boolean).join(' · ')}</div>
        </div>
        <div className="right">
          <h1>{kind === 'vehicle' ? (paid ? 'INVOICE' : 'WORK ORDER') : paid ? 'INVOICE' : 'REPAIR TICKET'}</h1>
          <div className="strong" style={{ fontSize: 17 }}>{p.ticket}</div>
          <div>{formatDateTime(p.created_at, p.timezone ?? undefined)}</div>
          <div className="barc"><Barcode value={p.ticket} height={40} /></div>
        </div>
      </div>
      <div className="two">
        <div>
          <div className="lab">Customer</div>
          <div className="strong">{p.customer.name}</div>
          <div>{formatPhone(p.customer.phone)}</div>
        </div>
        <div>
          <div className="lab">{kind === 'vehicle' ? 'Vehicle' : JOB_WORDS[kind].Item}</div>
          <div className="strong">{p.device.name}{p.device.color ? ` · ${p.device.color}` : ''}</div>
          {v ? (
            <>
              {v.vin ? <div>VIN {v.vin}</div> : null}
              {v.plate ? <div>Plate {v.plate}</div> : null}
              <div>Odometer in {v.odometer_in != null ? `${v.odometer_in.toLocaleString('en-US')} mi` : '—'} · out {v.odometer_out != null ? `${v.odometer_out.toLocaleString('en-US')} mi` : '—'}</div>
            </>
          ) : p.device.serial ? <div>Serial {p.device.serial}</div> : null}
          {p.promised_at ? <div>Ready by {formatDateTime(p.promised_at, p.timezone ?? undefined)}</div> : null}
        </div>
      </div>
      <div className="two">
        <div><div className="lab">{kind === 'vehicle' ? 'Customer’s concern' : 'Problem'}</div><div>{p.issue}</div>{p.condition_notes ? <><div className="lab" style={{ marginTop: 8 }}>Condition at drop-off</div><div>{p.condition_notes}</div></> : null}</div>
        <div>{checks.length ? <><div className="lab">Drop-off check</div><div>{checks.join(' · ')}</div></> : null}{p.left_with.length ? <div>Left with it: {p.left_with.join(', ')}</div> : null}</div>
      </div>
      {j.diagnosis ? <div className="wo-findings"><div className="lab">{kind === 'vehicle' ? 'Inspection findings' : 'Diagnosis'}</div><div style={{ whiteSpace: 'pre-wrap' }}>{j.diagnosis}</div></div> : null}
      {parts.length ? (
        <table>
          <thead><tr><th>Part</th><th className="r">Qty</th><th className="r">Each</th><th className="r">Amount</th></tr></thead>
          <tbody>{parts.map((l, i) => <tr key={i}><td className="strong">{l.description}</td><td className="r">{l.qty ?? 1}</td><td className="r">{formatCents(Math.round(l.price_cents / (l.qty || 1)))}</td><td className="r">{formatCents(l.price_cents)}</td></tr>)}</tbody>
        </table>
      ) : null}
      {labor.length ? (
        <table>
          <thead><tr><th>Labor</th><th className="r">Hours</th><th className="r">Amount</th></tr></thead>
          <tbody>{labor.map((l, i) => <tr key={i}><td className="strong">{l.description}</td><td className="r">{l.hours ?? ''}</td><td className="r">{formatCents(l.price_cents)}</td></tr>)}</tbody>
        </table>
      ) : null}
      <div className="totals">
        <div><span>Parts</span><span>{formatCents(p.totals.parts_cents)}</span></div>
        <div><span>Labor</span><span>{formatCents(p.totals.labor_cents)}</span></div>
        <div><span>{p.totals.tax_removed ? 'Tax removed' : 'Sales tax (parts)'}</span><span>{formatCents(p.totals.tax_cents)}</span></div>
        <div className="grand"><span>{paid ? 'Total paid' : 'Estimate'}</span><span>{formatCents(p.totals.total_cents)}</span></div>
        {paid ? <div>Paid · receipt #{p.paid.receipt_no}</div> : null}
      </div>
      <div className="wo-approval">
        <div className="lab">Customer authorization</div>
        {answers.map((a) => (
          <div key={a.id}>
            {a.decision === 'approved' ? 'Approved' : 'Declined'} {formatCents(a.total_cents)} on {formatDateTime(a.created_at, p.timezone ?? undefined)} · {a.method_label} · {a.customer_name}{a.note ? ` · ${a.note}` : ''}
            {a.signature_url ? <div><img className="wo-sig" src={a.signature_url} alt="Signature" /></div> : null}
          </div>
        ))}
        {!paid && (j.needs_approval || !answers.length) ? (
          <div>
            {j.approved_total_cents != null ? `I authorize the additional work, for the new estimate of ${formatCents(p.totals.total_cents)}.` : 'I authorize the work above for the estimate shown.'} No other work or charges without my OK.
            <div className="wo-signline"><span>Signature</span><span>Date</span></div>
          </div>
        ) : null}
      </div>
      <ReceiptPolicies title={kind === 'vehicle' ? 'WORK ORDER TERMS' : 'REPAIR POLICIES'} text={p.terms} />
      <SiteLine website={p.website} />
      <ReceiptMark />
    </div>
  );
}

/** Today's job words for screens outside this file. */
export function useJobKind(): JobKind | null {
  const plan = usePlan();
  return plan.industry === 'auto' ? 'vehicle' : plan.industry === 'vacuum' ? 'vacuum' : plan.industry === 'computer' ? 'computer' : null;
}

